import dayjs from 'dayjs';
import styled from 'styled-components';
import React, { FC, Fragment, useEffect } from 'react';
import { compose } from 'react-recompose';
import { connect, useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import { Alert, EmptyState, palette } from '@dev/ee-components';

import { ApplicationState } from '@/store';
import { SegmentsLoadHistoryTrackedStartAction } from '@/store/segments/actions';
import { ListsLoadHistoryTrackedStartAction } from '@/store/lists/actions';
import { getConfigStore } from '@/store/config/actions';
import { links } from '@/services/dictionaries/links';
import { useSecurity } from '@/services/hooks/useSecurity';

import LineChartPannel from './LineChartPannel';
import ContactsHistoryProps, { DispatchProps } from './ContactsHistoryInterface';

const InfoWrapper = styled.div`
    background: ${palette.basic.paper};
    padding: 40px 46px;
    border-radius: 0 0 4px 4px;
    &.board {
        margin-top: 0;
        border-top: none;
    }
    &.board-margin {
        margin-top: 32px;
    }
`;

const EmptyBoard = styled.div`
    background: ${palette.basic.paper};
    padding: 32px 0;
    margin-top: 32px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
`;

const styles = {
    root: {
        display: 'inline-block',
        marginRight: '2px',
    },
};

const actions = { SegmentsLoadHistoryTrackedStartAction, ListsLoadHistoryTrackedStartAction };

type Props = ContactsHistoryProps & DispatchProps;

/**
 *
 * @param props Contact History Props
 * @returns Contact History screen
 */

const ContactsHistory: FC<Props> = (props: Props): JSX.Element => {
    const { SegmentsLoadHistoryTrackedStartAction, ListsLoadHistoryTrackedStartAction, lists, segments, t } = props;
    const { isStandalone } = useSelector(getConfigStore);
    const { isReseller } = useSecurity();
    const dateNow = dayjs();
    const from = dateNow.subtract(6, 'day').startOf('day').format();
    const to = dateNow.endOf('day').format();
    useEffect((): void => {
        // Load history on each component mount to ensure tracking data is up to date
        SegmentsLoadHistoryTrackedStartAction(true, from, to);
        ListsLoadHistoryTrackedStartAction(true, from, to);
    }, []);

    return (
        <>
            <InfoWrapper className={isStandalone ? 'board-margin' : 'board'}>
                <Alert
                    severity="info"
                    body={
                        <span>
                            {`${t('turnonhistorytracking')} `}
                            {!isReseller && (
                                <span>
                                    {t('tolearnmorevisitour')}{' '}
                                    <a href={links.contactHistory} rel="noreferrer" target="_blank">
                                        {t('quickguide')}
                                    </a>
                                    .
                                </span>
                            )}
                        </span>
                    }
                />
            </InfoWrapper>
            {segments.historyTrackedCollection?.length ? (
                <LineChartPannel
                    loadSegmentsFunction={SegmentsLoadHistoryTrackedStartAction}
                    title={t('segments')}
                    graphsData={segments.historyTrackedCollection}
                />
            ) : (
                ''
            )}
            {lists.historyTrackedCollection?.length ? (
                <LineChartPannel
                    loadSegmentsFunction={ListsLoadHistoryTrackedStartAction}
                    title={t('lists')}
                    graphsData={lists.historyTrackedCollection}
                />
            ) : (
                ''
            )}
            {!segments.historyTrackedCollection?.length && !lists.historyTrackedCollection?.length && (
                <EmptyBoard className="board">
                    <EmptyState text={t('notrackinghistorytodisplay')} />
                </EmptyBoard>
            )}
        </>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: Props): Props => ({
    ...ownProps,
    segments: state.segments,
    lists: state.lists,
});

export default compose(
    withTranslation(),
    withStyles(styles),
    connect<ContactsHistoryProps, DispatchProps>(mapStateToProps, actions)
)(ContactsHistory);
